import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { useShowAds, useAdsSuppressed } from "@/hooks/useShowAds";
import { AD_UNIT_BANNER, USE_TEST_ADS, BANNER_ENABLED, initAds, initAppOpen, setAppOpenEnabled } from "@/lib/ads";

// BottomNav height in px (matches the banner reserve in App.tsx). Only relevant
// when BANNER_ENABLED is on.
const NAV_OFFSET_PX = 64;

/**
 * Ads controller for free (ad-supported) users on native. Initializes the AdMob
 * SDK + UMP consent and preloads the interstitial (shown after a sale via
 * `maybeShowInterstitial`). Optionally also shows a bottom banner when
 * BANNER_ENABLED. Renders nothing in the DOM; no-op on web and for paid users.
 */
export function AdSlot() {
  const showAds = useShowAds();
  const suppressed = useAdsSuppressed();

  // Init the SDK + UMP consent + preload the interstitial + start app-open ads.
  // When the user becomes paid (showAds=false), disable app-open.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (showAds) {
      initAds();
      initAppOpen();
    } else {
      setAppOpenEnabled(false);
    }
  }, [showAds]);

  // Optional persistent banner — disabled by default (interstitials are primary).
  const bannerActive = showAds && !suppressed && BANNER_ENABLED;
  useEffect(() => {
    if (!bannerActive || !Capacitor.isNativePlatform()) return;

    (async () => {
      try {
        const { AdMob, BannerAdSize, BannerAdPosition } = await import("@capacitor-community/admob");
        await AdMob.showBanner({
          adId: AD_UNIT_BANNER,
          adSize: BannerAdSize.ADAPTIVE_BANNER,
          position: BannerAdPosition.BOTTOM_CENTER,
          margin: NAV_OFFSET_PX,
          isTesting: USE_TEST_ADS,
        });
      } catch (err) {
        console.warn("[ads] showBanner failed", err);
      }
    })();

    return () => {
      import("@capacitor-community/admob")
        .then(({ AdMob }) => AdMob.removeBanner())
        .catch(() => {});
    };
  }, [bannerActive]);

  return null;
}

export default AdSlot;
